export type Theme = 'insufficient' | 'acceptable' | 'excellent';

export type QunoscoreIndex = {
  id: string;
  label: string;
  max: number;
  criteria: Record<string, Theme | undefined>;
  min: Record<string, number | undefined>;
  criteriaType?: 'percentile' | 'range';
  comparison?: 'TPCAllTime' | 'TPCLastYear';
  dataType?: 'years';
  keys?: Record<string, string | undefined>;
};

export type QunoscoreEntry = {
  title: string;
  description: string;
  indexes: QunoscoreIndex[];
};

const qunoScoreData: QunoscoreEntry[] = [
  {
    title: 'doctorProfile.qunoscoreSection.block.qualification',
    description:
      'doctorProfile.qunoscoreSection.block.qualification.description',
    indexes: [
      {
        id: 'topRankedUniversity',
        label:
          'doctorProfile.qunoscoreSection.block.qualification.topRankedUniversity',
        max: 1,
        criteria: { false: 'acceptable', true: 'excellent' },
        min: { false: 0, true: 1 },
      },
      {
        id: 'treatmentsPerformed',
        label:
          'doctorProfile.qunoscoreSection.block.qualification.treatmentsPerformed',
        max: 3,
        criteria: {
          '80-100': 'excellent',
          '31-79': 'acceptable',
          '0-30': 'insufficient',
        },
        min: {
          '80-100': 3,
          '51-79': 2,
          '31-50': 1,
          '0-30': 0,
        },
        criteriaType: 'percentile',
        comparison: 'TPCAllTime',
      },
      {
        id: 'treatmentsPerformedLastYear',
        label:
          'doctorProfile.qunoscoreSection.block.qualification.treatmentsPerformedLastYear',
        max: 3,
        criteria: {
          '80-100': 'excellent',
          '31-79': 'acceptable',
          '0-30': 'insufficient',
        },
        min: {
          '80-100': 3,
          '51-79': 2,
          '31-50': 1,
          '0-30': 0,
        },
        criteriaType: 'percentile',
        comparison: 'TPCLastYear',
      },
      {
        id: 'yearsOfExperienceAsDoctor',
        label:
          'doctorProfile.qunoscoreSection.block.qualification.yearsOfExperienceAsDoctor',
        max: 3,
        criteria: {
          '13-.': 'excellent',
          '6-12': 'acceptable',
          '0-5': 'insufficient',
        },
        min: {
          '13-.': 3,
          '9-12': 2,
          '6-8': 1,
          '0-5': 0,
        },
        criteriaType: 'range',
        dataType: 'years',
      },
      {
        id: 'relevantSpecialization',
        label:
          'doctorProfile.qunoscoreSection.block.qualification.relevantSpecialization',
        max: 1,
        criteria: { false: 'acceptable', true: 'excellent' },
        min: { false: 0, true: 1 },
      },
      {
        id: 'scientificActivity',
        label:
          'doctorProfile.qunoscoreSection.block.qualification.scientificActivity',
        max: 1,
        criteria: { false: 'acceptable', true: 'excellent' },
        min: { false: 0, true: 1 },
      },
      {
        id: 'specialistSociety',
        label:
          'doctorProfile.qunoscoreSection.block.qualification.specialistSociety',
        max: 1,
        criteria: { false: 'acceptable', true: 'excellent' },
        min: { false: 0, true: 1 },
      },
    ],
  },
  {
    title: 'doctorProfile.qunoscoreSection.block.qualificationOfService',
    description:
      'doctorProfile.qunoscoreSection.block.qualificationOfService.description',
    indexes: [
      {
        id: 'appointmentAvailability',
        label:
          'doctorProfile.qunoscoreSection.block.qualificationOfService.appointmentAvailability',
        max: 2,
        criteria: {
          'In 2-4 weeks': 'acceptable',
          'In less than 1 week': 'excellent',
          'In more than 4 weeks': 'insufficient',
        },
        min: {
          'In 2-4 weeks': 1,
          'In less than 1 week': 2,
          'In more than 4 weeks': 0,
        },
        keys: {
          'In 2-4 weeks': 'doctorProfile.qunoscoreSection.2_4Weeks',
          'In less than 1 week': 'doctorProfile.qunoscoreSection.lessThan1Week',
          'In more than 4 weeks':
            'doctorProfile.qunoscoreSection.moreThan4Weeks',
        },
      },
      {
        id: 'responsiveness',
        label:
          'doctorProfile.qunoscoreSection.block.qualificationOfService.responsiveness',
        max: 2,
        criteria: {
          Excellent: 'excellent',
          Good: 'acceptable',
          Moderate: 'acceptable',
        },
        min: {
          Excellent: 2,
          Good: 1,
          Moderate: 0,
        },
        keys: {
          Excellent: 'doctorProfile.qunoscoreSection.excellent',
          Good: 'doctorProfile.qunoscoreSection.good',
          Moderate: 'doctorProfile.qunoscoreSection.moderate',
        },
      },
      {
        id: 'englishSpoken',
        label:
          'doctorProfile.qunoscoreSection.block.qualificationOfService.englishSpoken',
        max: 3,
        criteria: {
          no: 'insufficient',
          'no but translator on site': 'acceptable',
          'yes basic': 'acceptable',
          yes: 'excellent',
        },
        min: {
          no: 0,
          'no but translator on site': 1,
          'yes basic': 2,
          yes: 3,
        },
        keys: {
          no: 'doctorProfile.qunoscoreSection.no',
          yes: 'doctorProfile.qunoscoreSection.yesExcellent',
          'yes basic': 'doctorProfile.qunoscoreSection.yesBasic',
          'no but translator on site':
            'doctorProfile.qunoscoreSection.noButTranslatorOnSite',
        },
      },
    ],
  },
  {
    title: 'doctorProfile.qunoscoreSection.block.valueForMoney',
    description:
      'doctorProfile.qunoscoreSection.block.valueForMoney.description',
    indexes: [
      {
        id: 'priceLevel',
        label: 'doctorProfile.qunoscoreSection.block.valueForMoney.priceLevel',
        max: 2,
        criteria: {
          'above average': 'insufficient',
          average: 'acceptable',
          excellent: 'excellent',
        },
        min: {
          'above average': 0,
          average: 1,
          excellent: 2,
        },
        keys: {
          excellent: 'doctorProfile.qunoscoreSection.excellent',
          average: 'doctorProfile.qunoscoreSection.average',
          'above average': 'doctorProfile.qunoscoreSection.aboveAverage',
        },
      },
      {
        id: 'discountForFollowUpTreatment',
        label:
          'doctorProfile.qunoscoreSection.block.valueForMoney.discountForFollowUpTreatment',
        max: 1,
        criteria: { false: 'acceptable', true: 'excellent' },
        min: { false: 0, true: 1 },
      },
      {
        id: 'discountForCompanion',
        label:
          'doctorProfile.qunoscoreSection.block.valueForMoney.discountForCompanion',
        max: 1,
        criteria: { false: 'acceptable', true: 'excellent' },
        min: { false: 0, true: 1 },
      },
      {
        id: 'accommodationIncluded',
        label:
          'doctorProfile.qunoscoreSection.block.valueForMoney.accommodationIncluded',
        max: 1,
        criteria: { false: 'acceptable', true: 'excellent' },
        min: { false: 0, true: 1 },
      },
      {
        id: 'groundTransferIncluded',
        label:
          'doctorProfile.qunoscoreSection.block.valueForMoney.groundTransferIncluded',
        max: 1,
        criteria: { false: 'acceptable', true: 'excellent' },
        min: { false: 0, true: 1 },
      },
      {
        id: 'afterCareIncluded',
        label:
          'doctorProfile.qunoscoreSection.block.valueForMoney.afterCareIncluded',
        max: 1,
        criteria: { false: 'acceptable', true: 'excellent' },
        min: { false: 0, true: 1 },
      },
    ],
  },
  {
    title: 'doctorProfile.qunoscoreSection.block.patientReviews',
    description:
      'doctorProfile.qunoscoreSection.block.patientReviews.description',
    indexes: [
      {
        id: 'qunoReviews',
        label:
          'doctorProfile.qunoscoreSection.block.patientReviews.qunoReviews',
        max: 2,
        criteria: {
          '1-3.9': 'insufficient',
          '4-4.6': 'acceptable',
          '4.7-5': 'excellent',
        },
        min: {
          '1-3.9': 0,
          '4-4.6': 1,
          '4.7-5': 2,
        },
        criteriaType: 'range',
      },
      {
        id: 'publicReviews',
        label:
          'doctorProfile.qunoscoreSection.block.patientReviews.publicReviews',
        max: 2,
        criteria: {
          '1-3.9': 'insufficient',
          '4-4.6': 'acceptable',
          '4.7-5': 'excellent',
        },
        min: {
          '1-3.9': 0,
          '4-4.6': 1,
          '4.7-5': 2,
        },
        criteriaType: 'range',
      },
    ],
  },
];

export default qunoScoreData;
